import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "../../utilities/helpers";
import { useCurrency } from "../dropdown";
import {
	buildInvestmentYears,
	formatCurrency,
	formatSignedCurrency,
	formatSignedPercent,
	useAssetPalette,
	useDailyQuery,
	useDividendsQuery,
	useInvestmentsQuery,
	useLatestPricesQuery,
} from "./shared";

const YEAR_GRID =
	"lg:grid-cols-[minmax(0,1.35fr)_repeat(5,minmax(0,1fr))_8.5rem]";

const formatShortDate = (date) =>
	date.toLocaleDateString("en-GB", {
		day: "numeric",
		month: "short",
		year: "numeric",
		timeZone: "UTC",
	});

const formatUnits = (units) =>
	new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 }).format(units);

const toneClass = (value) => {
	if (value > 0.005) return "text-emerald-700";
	if (value < -0.005) return "text-rose-700";
	return "text-slate-600";
};

const ProfitLoss = ({ value, percent, currency, className }) => (
	<div className={cn("tabular-nums", toneClass(value), className)}>
		<div className="font-medium">{formatSignedCurrency(value, currency)}</div>
		<div className="text-xs">{percent == null ? "—" : formatSignedPercent(percent)}</div>
	</div>
);

const Metric = ({ label, children, className }) => (
	<div className={cn("min-w-0 lg:text-right", className)}>
		<dt className="text-xs text-slate-500 lg:sr-only">{label}</dt>
		<dd className="mt-0.5 text-sm tabular-nums text-slate-900 lg:mt-0">{children}</dd>
	</div>
);

const Ladder = () => {
	const { currency } = useCurrency();
	const paletteFor = useAssetPalette();
	const [expandedYears, setExpandedYears] = useState({});
	const { data: investments, isLoading } = useInvestmentsQuery();
	const { data: daily } = useDailyQuery();
	const { data: latestPrices } = useLatestPricesQuery();
	const { data: dividends } = useDividendsQuery();

	const years = useMemo(() => {
		return buildInvestmentYears({ investments, daily, latestPrices, dividends })
			.map((row) => {
				// amountInvested = previous year-end worth + this year's net cash flow.
				const startValue = row.assets.reduce((sum, asset) => sum + asset.startingValue, 0);
				return {
					year: row.year,
					startDate: row.startDate,
					endDate: row.endDate,
					isInProgress: !row.isClosed,
					startValue,
					netNewCapital: row.amountInvested - startValue,
					endValue: row.finalWorth,
					dividends: row.dividendIncome,
					profitLoss: row.profitLoss,
					profitLossPercent:
						row.amountInvested > 0 ? (row.profitLoss / row.amountInvested) * 100 : null,
					assets: row.assets.map((asset) => ({
						asset: asset.asset,
						label: asset.label,
						startingValue: asset.startingValue,
						invested: asset.invested,
						endingValue: asset.endingValue,
						dividendIncome: asset.dividendIncome,
						profitLoss: asset.profitLoss,
						profitLossPercent:
							asset.amountInvested > 0 ? asset.profitLossPercent : null,
						endingUnits: asset.endingUnits,
					})),
				};
			})
			.reverse();
	}, [daily, dividends, investments, latestPrices]);

	const toggleYear = (year) => {
		setExpandedYears((current) => ({ ...current, [year]: !current[year] }));
	};

	if (isLoading && !investments) {
		return (
			<div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:p-6">
				{Array.from({ length: 2 }).map((_, index) => (
					<div key={index} className="h-12 animate-pulse rounded bg-slate-100" />
				))}
			</div>
		);
	}

	if (!years.length) {
		return (
			<div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500 shadow-sm">
				No investments recorded yet.
			</div>
		);
	}

	return (
		<div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
			<div
				aria-hidden="true"
				className={cn(
					"hidden border-b border-slate-200 px-6 py-2.5 text-xs font-medium text-slate-500 lg:grid lg:gap-x-4",
					YEAR_GRID
				)}
			>
				<div>Investment year</div>
				<div className="text-right">Start value</div>
				<div className="text-right">Net new capital</div>
				<div className="text-right">End value</div>
				<div className="text-right">Dividends</div>
				<div className="text-right">P/L</div>
				<div />
			</div>

			<ul>
				{years.map((year) => {
					const isExpanded = Boolean(expandedYears[year.year]);
					const panelId = `ladder-year-${year.year}-assets`;
					const range = `${formatShortDate(year.startDate)} – ${
						year.isInProgress ? "today" : formatShortDate(year.endDate)
					}`;

					return (
						<li key={year.year} className="border-b border-slate-200 last:border-b-0">
							<div
								className={cn(
									"grid grid-cols-2 gap-x-4 gap-y-3 px-4 py-4 sm:grid-cols-4 lg:items-center lg:gap-y-0 lg:px-6",
									YEAR_GRID
								)}
							>
								<div className="col-span-2 flex items-start justify-between gap-3 sm:col-span-4 lg:col-span-1">
									<div className="min-w-0">
										<div className="flex flex-wrap items-center gap-2">
											<h3 className="text-base font-semibold tabular-nums text-slate-900">
												{year.year}
											</h3>
											{year.isInProgress && (
												<span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700 ring-1 ring-inset ring-sky-600/20">
													In progress
												</span>
											)}
										</div>
										<p className="text-xs text-slate-500">{range}</p>
									</div>
									<ProfitLoss
										value={year.profitLoss}
										percent={year.profitLossPercent}
										currency={currency}
										className="text-right lg:hidden"
									/>
								</div>

								<dl className="contents">
									<Metric label="Start value">
										{formatCurrency(year.startValue, currency)}
									</Metric>
									<Metric label="Net new capital">
										{formatSignedCurrency(year.netNewCapital, currency)}
									</Metric>
									<Metric label={year.isInProgress ? "Current value" : "End value"}>
										<span className="font-medium">
											{formatCurrency(year.endValue, currency)}
										</span>
									</Metric>
									<Metric label="Dividends">
										{formatCurrency(year.dividends, currency)}
									</Metric>
									<div className="hidden lg:block lg:text-right">
										<dt className="sr-only">Profit / loss</dt>
										<dd>
											<ProfitLoss
												value={year.profitLoss}
												percent={year.profitLossPercent}
												currency={currency}
											/>
										</dd>
									</div>
								</dl>

								<div className="col-span-2 sm:col-span-4 lg:col-span-1 lg:flex lg:justify-end">
									<button
										type="button"
										onClick={() => toggleYear(year.year)}
										aria-expanded={isExpanded}
										aria-controls={panelId}
										className="inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 sm:w-auto"
									>
										<ChevronDown
											aria-hidden="true"
											className={cn(
												"h-4 w-4 text-slate-500 transition-transform",
												isExpanded && "rotate-180"
											)}
										/>
										{isExpanded ? "Hide assets" : "Assets"}
										<span className="sr-only"> for {year.year}</span>
										{!isExpanded && (
											<span className="tabular-nums text-slate-400">{year.assets.length}</span>
										)}
									</button>
								</div>
							</div>

							<div
								id={panelId}
								hidden={!isExpanded}
								className="border-t border-slate-200 bg-slate-50 px-4 py-4 lg:px-6"
							>
								<p className="mb-3 text-xs text-slate-500">
									P/L = end value − (start value + net invested) + dividends.
								</p>
								<div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
									<table className="w-full min-w-[760px] border-collapse text-sm">
										<caption className="sr-only">Per-asset performance for {year.year}</caption>
										<thead>
											<tr className="border-b border-slate-200 text-xs text-slate-500">
												<th
													scope="col"
													className="sticky left-0 z-10 bg-white px-4 py-2.5 text-left font-medium shadow-[inset_-1px_0_0_#e2e8f0] md:shadow-none"
												>
													Asset
												</th>
												<th scope="col" className="px-4 py-2.5 text-right font-medium">
													Start value
												</th>
												<th scope="col" className="px-4 py-2.5 text-right font-medium">
													Net invested
												</th>
												<th scope="col" className="px-4 py-2.5 text-right font-medium">
													{year.isInProgress ? "Current value" : "End value"}
												</th>
												<th scope="col" className="px-4 py-2.5 text-right font-medium">
													Dividends
												</th>
												<th scope="col" className="px-4 py-2.5 text-right font-medium">
													P/L
												</th>
												<th scope="col" className="px-4 py-2.5 text-right font-medium">
													End units
												</th>
											</tr>
										</thead>
										<tbody>
											{year.assets.map((asset) => {
												const palette = paletteFor(asset.asset);
												return (
													<tr
														key={asset.asset}
														className="group border-b border-slate-100 transition-colors last:border-b-0 hover:bg-slate-50"
													>
														<th
															scope="row"
															className="sticky left-0 z-10 bg-white px-4 py-3 text-left font-medium text-slate-900 shadow-[inset_-1px_0_0_#e2e8f0] transition-colors group-hover:bg-slate-50 md:shadow-none"
														>
															<div className="flex items-center gap-2.5">
																<span
																	aria-hidden="true"
																	className={cn("h-2.5 w-2.5 shrink-0 rounded-full", palette.dot)}
																/>
																{asset.label}
															</div>
														</th>
														<td className="px-4 py-3 text-right tabular-nums text-slate-600">
															{formatCurrency(asset.startingValue, currency)}
														</td>
														<td className="px-4 py-3 text-right tabular-nums text-slate-600">
															{formatSignedCurrency(asset.invested, currency)}
														</td>
														<td className="px-4 py-3 text-right tabular-nums text-slate-900">
															{formatCurrency(asset.endingValue, currency)}
														</td>
														<td className="px-4 py-3 text-right tabular-nums text-slate-600">
															{formatCurrency(asset.dividendIncome, currency)}
														</td>
														<td className="px-4 py-3 text-right">
															<ProfitLoss
																value={asset.profitLoss}
																percent={asset.profitLossPercent}
																currency={currency}
															/>
														</td>
														<td className="px-4 py-3 text-right tabular-nums text-slate-600">
															{formatUnits(asset.endingUnits)}
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							</div>
						</li>
					);
				})}
			</ul>
		</div>
	);
};

export default Ladder;
